import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Table } from '@quintoandar-tokko/jopijs';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import parentStyles from '../dashboardTables.module.scss';
import specificStyles from './closedLeadsTable.module.scss';
import LoadingMoreSkeleton from '../components/LoadingMoreSkeleton';
import buildQuery from '../../utils/buildQuery';
import useObserver from '@/hooks/useObserver';
import { setPerformanceModals } from '@/store/slices/components/dashboard/DashboardSlice';
import HeaderCell from '../components/HeaderCell';
import buildTableQuery from '../../utils/buildTableQuery';
import { TABLE_SORT_FIELDS } from '../../constants';
import ClosedLeadsTableRow from './ClosedLeadsTableRow';
import useTrackPerformanceTable from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';
import { getPerformanceClosedLeadsTable } from '@/store/slices/components/dashboard/service';

export default function ClosedLeadsTable() {
  const dispatch = useDispatch();
  const { selectedIds } = useSelector((state) => state.FilterBarSlice.filters);
  const { closedLeads } = useSelector(
    (state) => state.DashboardSlice.performanceModals
  );
  const { ref: loadMoreRef, isInViewport } = useObserver();
  const track = useTrackPerformanceTable();

  const headers = [
    {
      id: 1,
      title: Trans(translations, 'Contact'),
      field: TABLE_SORT_FIELDS.CONTACT_NAME,
    },
    {
      id: 2,
      title: Trans(translations, 'Agent'),
      subtitle: Trans(translations, 'Branch'),
      field: TABLE_SORT_FIELDS.AGENT_NAME,
    },
    {
      id: 3,
      title: Trans(translations, 'ContactOperations.FollowUpDays'),
      field: TABLE_SORT_FIELDS.DAYS_OF_FOLLOW_UP,
    },
    {
      id: 4,
      title: Trans(translations, 'ClosureReason'),
      field: TABLE_SORT_FIELDS.QUALIFICATION,
    },
    {
      id: 5,
      title: Trans(translations, 'DashboardTableColumn.Income'),
      subtitle: Trans(translations, 'DashboardTableColumn.Closure'),
      field: TABLE_SORT_FIELDS.CLOSE_DATE,
    },
  ];

  useEffect(() => {
    if (!closedLeads.has_next) return;
    if (!isInViewport || closedLeads.loading) return;

    const query = buildQuery(selectedIds);
    const tableQuery = buildTableQuery(closedLeads, {
      withNextPage: true,
    });

    dispatch(getPerformanceClosedLeadsTable({ query, tableQuery }));
  }, [isInViewport]);

  const handleCellClick = (header) => {
    if (!header.field) return;

    dispatch(
      setPerformanceModals({
        key: 'closedLeads',
        subkey: 'sortBy',
        value: header.field,
      })
    );
    track.closedLeads(TRACK_ELEMENT.COLUMN_ORDER);
  };

  return (
    <Table
      id="closed-leads-table"
      className={`${parentStyles.table} ${specificStyles.closedLeadsTable}`}
    >
      <Table.Header className={parentStyles.tableHeader}>
        {headers.map((header) => (
          <HeaderCell
            key={header.id}
            id={header.id}
            title={header.title}
            subtitle={header.subtitle}
            field={header.field}
            sortBy={closedLeads.sortBy}
            sortDirection={closedLeads.sortDirection}
            onClick={() => handleCellClick(header)}
          />
        ))}
      </Table.Header>

      <Table.Rows>
        {closedLeads.list.map((row) => (
          <ClosedLeadsTableRow key={row.id} row={row} />
        ))}

        <Box as="tr" ref={loadMoreRef} style={{ height: 1, opacity: 0 }} />

        {(closedLeads.loading || closedLeads.has_next) && (
          <LoadingMoreSkeleton columns={headers.length} />
        )}
      </Table.Rows>
    </Table>
  );
}
